/**
 * Deriving what the device detail page shows.
 *
 * Same discipline as deviceViewModel, and the same governing requirement: an
 * offline device renders its last-seen time and its last-known camera state.
 * Never an error screen. There is no input to this module that produces
 * "something went wrong" — a device that has been seen once always has something
 * useful to say, and "this camera was streaming eleven minutes ago" is actionable
 * where "Error" is not.
 *
 * The page is where an operator decides what to do about a device, so most of the
 * work here is turning state into a sentence that names the next action.
 */

import {
  relativeTime,
  type CameraSummary,
  type DeviceHealth,
  type FetchState,
} from './deviceViewModel.ts';

/** Mirrors what the detail endpoint returns for one camera. */
export interface CameraDetail {
  id: string;
  name: string | null;
  onvif_host: string;
  sub_profile_uri: string;
  main_profile_uri: string | null;
  recording_enabled: boolean;
  state: CameraSummary['state'];
  has_credentials: boolean;
  credentials_updated_at: string | null;
}

export interface DeviceDetailData {
  id: string;
  name: string;
  status: string;
  last_seen_at: string | null;
  firmware_version: string | null;
  public_key: string | null;
  public_key_updated_at: string | null;
  publicKeyFingerprint: string | null;
  site_id: string | null;
  site_name: string | null;
  client_id: string | null;
  client_name: string | null;
  max_cameras: number | null;
  cloud_event_retention_days: number | null;
  entitlement_expires_at: string | null;
  /** What the cloud believes this device should be running. */
  config_revision: number | null;
  /** What the device last said it was running. Null means it has never said. */
  applied_config_revision: number | null;
  applied_config_at: string | null;
  cameras: CameraDetail[];
}

/**
 * Whether the device is running the config the cloud has for it.
 *
 * `unknown` is a distinct state and not a failure: firmware predating config
 * acknowledgement reports nothing, and a device that has just claimed has
 * applied nothing yet. Rendering either as "out of date" would send someone
 * investigating a device that is fine.
 */
export type ConfigState = 'current' | 'pending' | 'unknown';

export type EntitlementState = 'none' | 'active' | 'expiring' | 'expired';

export interface CameraRowModel {
  id: string;
  /** What to show. Falls back to the address, which at least identifies it. */
  displayName: string;
  /** Shown as a subtitle when a name is set, so the address stays visible. */
  address: string | null;
  state: CameraSummary['state'];
  stateLabel: string;
  recordingEnabled: boolean;
  hasCredentials: boolean;
  /** e.g. "Set 3 days ago", or the reason none can be set. */
  credentialsLabel: string;
  /** False when the device has published no key, so the control is disabled. */
  canSetCredentials: boolean;
  subProfileUri: string;
  mainProfileUri: string | null;
}

export interface DeviceDetailViewModel {
  id: string;
  name: string;
  health: DeviceHealth;
  healthLabel: string;
  claimed: boolean;
  lastSeenLabel: string;
  siteName: string | null;
  clientName: string | null;
  firmwareLabel: string;
  /** Whether the device has published an encryption key, and what that means. */
  keyLabel: string;
  keyFingerprintShort: string | null;
  canSetCredentials: boolean;
  entitlement: {
    state: EntitlementState;
    label: string;
    used: number;
    max: number | null;
    /** False when the account has lapsed or is at capacity. */
    canAddCamera: boolean;
    /** Why not, when canAddCamera is false. */
    blockedReason: string | null;
    retentionLabel: string;
  };
  cameras: CameraRowModel[];
  config: {
    state: ConfigState;
    label: string;
  };
  /** True when the data on screen is older than it should be. A marker, never a replacement. */
  stale: boolean;
  staleReason: string | null;
}

function configStateOf(device: DeviceDetailData, now: number): { state: ConfigState; label: string } {
  const expected = device.config_revision;
  const applied = device.applied_config_revision;

  if (expected === null || applied === null) {
    return {
      state: 'unknown',
      label:
        applied === null && expected !== null
          ? 'Not yet confirmed by the device'
          : 'Not reported',
    };
  }

  if (applied >= expected) {
    const at = parseTime(device.applied_config_at);
    return {
      state: 'current',
      label: at === null ? 'Up to date' : `Up to date, confirmed ${relativeTime(at, now)}`,
    };
  }

  /*
   * Behind. Ordinary for a device that is offline or has just been changed —
   * the push is waiting for it, not lost — so the wording says what is
   * happening rather than raising an alarm.
   */
  return { state: 'pending', label: 'Waiting for the device to apply a change' };
}

const HEALTH_LABEL: Record<DeviceHealth, string> = {
  online: 'Online',
  offline: 'Offline',
  'never-connected': 'Never connected',
};

const CAMERA_STATE_LABEL: Record<CameraSummary['state'], string> = {
  unknown: 'Unknown',
  connecting: 'Connecting',
  streaming: 'Streaming',
  offline: 'Offline',
  error: 'Error',
};

/**
 * How long before expiry to start warning.
 *
 * Fourteen days, which is long enough to reach someone about a renewal and short
 * enough that the warning still means something when it appears.
 */
export const ENTITLEMENT_WARNING_MS = 14 * 24 * 60 * 60 * 1000;

const DEVICE_OFFLINE_AFTER_MS = 90_000;
const STALE_AFTER_MS = 60_000;

function parseTime(value: string | null): number | null {
  if (!value) return null;
  const parsed = Date.parse(value);
  // A timestamp the browser cannot parse is treated as absent rather than as
  // NaN, which would otherwise render "last seen NaN days ago".
  return Number.isNaN(parsed) ? null : parsed;
}

function healthOf(lastSeenAt: number | null, now: number): DeviceHealth {
  if (lastSeenAt === null) return 'never-connected';
  return now - lastSeenAt <= DEVICE_OFFLINE_AFTER_MS ? 'online' : 'offline';
}

function entitlementStateOf(expiresAt: number | null, max: number | null, now: number): EntitlementState {
  if (max === null) return 'none';
  if (expiresAt === null) return 'active';
  if (expiresAt <= now) return 'expired';
  return expiresAt - now <= ENTITLEMENT_WARNING_MS ? 'expiring' : 'active';
}

function credentialsLabelFor(camera: CameraDetail, canSet: boolean, now: number): string {
  if (!camera.has_credentials) {
    return canSet ? 'No credentials set' : 'No credentials — device has not published a key';
  }
  const updated = parseTime(camera.credentials_updated_at);
  /*
   * When, never what. The cloud cannot read a credential back, so this is the
   * only thing there is to say about one — and saying "set 3 days ago" is what
   * lets someone tell whether a rotation actually landed.
   */
  return updated === null ? 'Credentials set' : `Credentials set ${relativeTime(updated, now)}`;
}

export function deviceDetailViewModel(
  device: DeviceDetailData,
  fetch: FetchState,
  now: number,
): DeviceDetailViewModel {
  const lastSeenAt = parseTime(device.last_seen_at);
  const health = healthOf(lastSeenAt, now);

  const canSetCredentials = device.public_key !== null && device.publicKeyFingerprint !== null;

  const used = device.cameras.length;
  const max = device.max_cameras;
  const expiresAt = parseTime(device.entitlement_expires_at);
  const entitlementState = entitlementStateOf(expiresAt, max, now);

  let entitlementLabel: string;
  if (entitlementState === 'none') {
    entitlementLabel = 'No entitlement recorded';
  } else if (entitlementState === 'expired') {
    entitlementLabel = `Lapsed ${relativeTime(expiresAt as number, now)}`;
  } else if (entitlementState === 'expiring') {
    entitlementLabel = `Expires ${new Date(expiresAt as number).toLocaleDateString()}`;
  } else {
    entitlementLabel = expiresAt === null ? 'No expiry' : `Renews ${new Date(expiresAt).toLocaleDateString()}`;
  }

  /*
   * Capacity cannot grow on a lapsed account, and existing cameras are untouched.
   * Mirrors the server's refusal rather than guessing at it -- the server decides,
   * this only avoids offering a button that is going to be refused.
   */
  const atCapacity = max !== null && used >= max;
  const canAdd = entitlementState !== 'expired' && entitlementState !== 'none' && !atCapacity;

  let blockedReason: string | null = null;
  if (entitlementState === 'none') {
    blockedReason = 'This device has no entitlement recorded, so cameras cannot be added.';
  } else if (entitlementState === 'expired') {
    blockedReason =
      'This device’s entitlement has lapsed. Existing cameras keep recording, but no more can be added.';
  } else if (atCapacity) {
    blockedReason = `This device is licensed for ${max as number} cameras.`;
  }

  let stale = false;
  let staleReason: string | null = null;
  if (fetch.status === 'failed') {
    stale = true;
    staleReason =
      fetch.lastSucceededAt === null
        ? 'Not yet reached the server'
        : `Last updated ${relativeTime(fetch.lastSucceededAt, now)}`;
  } else if (fetch.status === 'ok' && now - fetch.fetchedAt > STALE_AFTER_MS) {
    stale = true;
    staleReason = `Last updated ${relativeTime(fetch.fetchedAt, now)}`;
  }

  return {
    id: device.id,
    name: device.name,
    health,
    healthLabel: HEALTH_LABEL[health],
    claimed: device.status !== 'unclaimed',
    lastSeenLabel: lastSeenAt === null ? 'Never connected' : relativeTime(lastSeenAt, now),
    siteName: device.site_name,
    clientName: device.client_name,
    firmwareLabel: device.firmware_version ?? 'Not reported',
    keyLabel: canSetCredentials
      ? 'Published — camera credentials are encrypted to this device'
      : 'Not published — the device has not claimed itself yet',
    // Enough to compare against what the agent logged, short enough to read out.
    keyFingerprintShort: device.publicKeyFingerprint?.slice(0, 12) ?? null,
    canSetCredentials,
    entitlement: {
      state: entitlementState,
      label: entitlementLabel,
      used,
      max,
      canAddCamera: canAdd,
      blockedReason,
      retentionLabel:
        device.cloud_event_retention_days === null || device.cloud_event_retention_days === 0
          ? 'No cloud retention'
          : `${device.cloud_event_retention_days} days of cloud events`,
    },
    config: configStateOf(device, now),
    cameras: device.cameras.map((camera) => ({
      id: camera.id,
      displayName: camera.name?.trim() || camera.onvif_host,
      // Only when it is not already the heading, so the row does not repeat itself.
      address: camera.name?.trim() ? camera.onvif_host : null,
      state: camera.state,
      stateLabel: CAMERA_STATE_LABEL[camera.state] ?? 'Unknown',
      recordingEnabled: camera.recording_enabled,
      hasCredentials: camera.has_credentials,
      credentialsLabel: credentialsLabelFor(camera, canSetCredentials, now),
      canSetCredentials,
      subProfileUri: camera.sub_profile_uri,
      mainProfileUri: camera.main_profile_uri,
    })),
    stale,
    staleReason,
  };
}
